
import TimeUnit from "./TimeUnit.js";
import ZonedClock from "./ZonedClock.js";
import RcMinute from "./RcMinute.js";
import RcHour from "./RcHour.js";
import RcDay from "./RcDay.js";
import RcMonth from "./RcMonth.js";
import RcYear from "./RcYear.js";

/**
 * A quarter of an hour, mirroring com.readychek.time.RcQuarterHour. The timesheet grid
 * divides a day into 96 of these, addressed by a time id.
 */
export default class RcQuarterHour extends TimeUnit {

  static Quarters = {
    ZERO: 0,
    ONE: 1,
    TWO: 2,
    THREE: 3,
    FOUR: 4,
  };

  constructor(date, timeZone) {
    super(date, timeZone);
  }

  /**
   * The quarter the given minute falls inside: 0 for :00-:14, 1 for :15-:29, 2 for :30-:44
   * and 3 for :45-:59. Always one of the four real quarters - only nearest() reaches past
   * the end of the hour, and it reports that by rolling into the next hour.
   */
  quarterFromMinute(minute) {
    if (minute < 15) {
      return RcQuarterHour.Quarters.ZERO;
    }
    if (minute < 30) {
      return RcQuarterHour.Quarters.ONE;
    }
    if (minute < 45) {
      return RcQuarterHour.Quarters.TWO;
    }
    return RcQuarterHour.Quarters.THREE;
  }

  /**
   * The quarter this instant is inside. Rounding is nearest()'s job, so a quarter hour that
   * has already been snapped reports the boundary it sits on.
   */
  value() {
    return this.quarterFromMinute(this.minute());
  }

  static nearestQuarterFromMinute(minute) {
    if (minute < 8) {
      return RcQuarterHour.Quarters.ZERO;
    }
    if (minute < 23) {
      return RcQuarterHour.Quarters.ONE;
    }
    if (minute < 38) {
      return RcQuarterHour.Quarters.TWO;
    }
    if (minute < 53) {
      return RcQuarterHour.Quarters.THREE;
    }
    return RcQuarterHour.Quarters.FOUR;
  }

  static nearestQuarterMinuteFromMinute(minute) {
    if (minute < 8) {
      return 0;
    }
    if (minute < 23) {
      return 15;
    }
    if (minute < 38) {
      return 30;
    }
    if (minute < 53) {
      return 45;
    }
    return 0;
  }

  /** The closest quarter boundary, rolling into the next hour past :53. */
  nearest() {
    const minute = this.minute();
    const nearestQuarter = RcQuarterHour.nearestQuarterFromMinute(minute);
    if (nearestQuarter === RcQuarterHour.Quarters.FOUR) {
      const startOfNextHour = ZonedClock.withFields(
        new Date(this.time() + 3600000), this.timeZone(),
        { minute: 0, second: 0, milli: 0 },
      );
      return new RcQuarterHour(startOfNextHour, this.timeZone());
    }
    const rounded = ZonedClock.withFields(this.date(), this.timeZone(), {
      minute: RcQuarterHour.nearestQuarterMinuteFromMinute(minute),
      second: 0,
      milli: 0,
    });
    return new RcQuarterHour(rounded, this.timeZone());
  }

  timeId() {
    return TimeUnit.newTimeId(this.hour().value(), this.value());
  }

  next() {
    return new RcQuarterHour(this.asMinute().add(15).date(), this.timeZone());
  }

  previous() {
    return new RcQuarterHour(this.asMinute().subtract(15).date(), this.timeZone());
  }

  asMinute() {
    return new RcMinute(this.date(), this.timeZone());
  }

  hour() {
    return new RcHour(this.date(), this.timeZone());
  }

  day() {
    return new RcDay(this.date(), this.timeZone());
  }

  month() {
    return new RcMonth(this.date(), this.timeZone());
  }

  year() {
    return new RcYear(this.date(), this.timeZone());
  }

  static quarterEnd(quarterValue) {
    if (quarterValue === RcQuarterHour.Quarters.ONE) {
      return RcQuarterHour.Quarters.TWO;
    }
    if (quarterValue === RcQuarterHour.Quarters.TWO) {
      return RcQuarterHour.Quarters.THREE;
    }
    if (quarterValue === RcQuarterHour.Quarters.THREE) {
      return RcQuarterHour.Quarters.FOUR;
    }
    return RcQuarterHour.Quarters.ONE;
  }

  static quarterStart(minutes) {
    if (minutes === 15) {
      return RcQuarterHour.Quarters.TWO;
    }
    if (minutes === 30) {
      return RcQuarterHour.Quarters.THREE;
    }
    if (minutes === 45) {
      return RcQuarterHour.Quarters.FOUR;
    }
    return RcQuarterHour.Quarters.ONE;
  }
}
